import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Checkbox, Radio, toast } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import { Trans, useTranslation } from "react-i18next";
import contactHttpClient from "utils/contactHttpClient";
import { useBrandingContext } from "context/BrandingContext";

/** The same quiet card as the signed-out prompt it replaces. */
const AutoPayPanel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Stack
        gap="var(--space-3)"
        sx={{ alignItems: "center", textAlign: "center" }}
      >
        {children}
      </Stack>
    </CardContent>
  </Card>
);

interface AutoPayManagerProps {
  recipientId: string | null;
  invoiceId: string | null;
}

const AutoPayManager: React.FC<AutoPayManagerProps> = ({
  recipientId,
  invoiceId,
}) => {
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showAutoPayModal, setShowAutoPayModal] = useState(false);
  const [autoPayAgreed, setAutoPayAgreed] = useState(false);
  const [currentAutoPayMethodId, setCurrentAutoPayMethodId] = useState<
    string | null
  >(null);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<any[]>(
    [],
  );

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  const isAutoPayEnabled = !!currentAutoPayMethodId;
  const hasSavedPaymentMethods = availablePaymentMethods.length > 0;
  const canEnableAutoPay = hasSavedPaymentMethods;

  const fetchPaymentMethods = async () => {
    if (!invoiceId || !recipientId) return;
    try {
      const response = await contactHttpClient.get(
        `/payments/customer-payment-methods/${invoiceId}/${recipientId}`,
        { suppressAuthToast: true },
      );

      // Only show payment methods that support auto-pay
      const autoPayEligibleMethods = (
        response.data.paymentMethods || []
      ).filter((method: any) => method.canAutoPayWith);

      setAvailablePaymentMethods(autoPayEligibleMethods);
      const newAutoPayMethodId = response.data.currentAutoPayMethodId || null;

      // CHECK IF CURRENT AUTO-PAY METHOD STILL EXISTS
      if (newAutoPayMethodId) {
        const autoPayMethodExists = autoPayEligibleMethods.some(
          (method: any) => method.id === newAutoPayMethodId,
        );

        if (!autoPayMethodExists) {
          // Auto-pay method was deleted! Remove it automatically
          toast.dismiss();
          toast.warning(
            t(
              "Your auto-pay payment method was removed. Please set up auto-pay again with a different payment method.",
            ),
          );

          await removeAutoPayMethodSilently();
          setCurrentAutoPayMethodId(null);
        } else {
          setCurrentAutoPayMethodId(newAutoPayMethodId);
        }
      } else {
        setCurrentAutoPayMethodId(null);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setAvailablePaymentMethods([]);
      setCurrentAutoPayMethodId(null);
    }
  };

  const removeAutoPayMethodSilently = async () => {
    try {
      await contactHttpClient.delete(
        `/payments/autopay/${invoiceId}/${recipientId}`,
      );
      return true;
    } catch (err) {
      console.error("Failed to remove invalid auto-pay method:", err);
      return false;
    }
  };

  const removeAutoPayMethod = async () => {
    setIsLoading(true);
    try {
      const response = await contactHttpClient.delete(
        `/payments/autopay/${invoiceId}/${recipientId}`,
      );
      if (response.data.success) {
        setCurrentAutoPayMethodId(null);
        toast.success(t("Auto pay has been removed"));
      } else {
        toast.error(response.data.error || t("Failed to remove auto pay"));
      }
    } catch (err) {
      toast.error("Failed to remove auto pay");
    } finally {
      setIsLoading(false);
    }
  };

  const setAutoPayMethod = async () => {
    if (!selectedPaymentMethodId) {
      toast.error(t("Please select a payment method"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await contactHttpClient.post("/payments/autopay", {
        invoiceId: invoiceId,
        paymentMethodId: selectedPaymentMethodId,
        recipientId: recipientId,
      });
      const result = await response.data;

      setShowAutoPayModal(false);
      if (result.success) {
        await fetchPaymentMethods();
        toast.success(t("AutoPayEnabledMessage"));
      } else {
        if (result.code === "PAYMENT_METHOD_NOT_REUSABLE") {
          toast.error(result.error || t("FailedEnableAutoPay"), {
            duration: 8000,
          });
        } else {
          toast.error(result.error || t("FailedEnableAutoPay"));
        }
      }
    } catch (err: any) {
      setShowAutoPayModal(false);
      const errorMessage =
        err.response?.data?.error || t("FailedEnableAutoPay");
      const errorCode = err.response?.data?.code;

      if (errorCode === "PAYMENT_METHOD_NOT_REUSABLE") {
        toast.error(errorMessage, { duration: 8000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatPaymentMethodDisplay = (method: any) => {
    if (method.type === "card" && method.details.brand) {
      return `${method.details.brand} ****${method.details.last4}`;
    } else if (method.type === "us_bank_account" && method.details.bank_name) {
      return `${method.details.bank_name} ****${method.details.last4}`;
    } else if (method.type === "link") {
      return `Link ****${method.details.last4}`;
    }
    return `${method.type} ****${method.details.last4}`;
  };

  const handleOpenModal = () => {
    setSelectedPaymentMethodId(
      currentAutoPayMethodId || availablePaymentMethods[0]?.id || null,
    );
    setAutoPayAgreed(false);
    setShowAutoPayModal(true);
  };

  const handleCloseModal = () => {
    setShowAutoPayModal(false);
    setAutoPayAgreed(false);
  };

  useEffect(() => {
    if (invoiceId && recipientId) {
      fetchPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, recipientId]);

  if (!recipientId) {
    return null;
  }

  const currentAutoPayMethod = availablePaymentMethods.find(
    (pm) => pm.id === currentAutoPayMethodId,
  );

  const showAutoPayInformation =
    !isAutoPayEnabled || selectedPaymentMethodId !== currentAutoPayMethodId;

  return (
    <>
      {isAutoPayEnabled && currentAutoPayMethod ? (
        <AutoPayPanel>
          <Typography variant="subtitle1">
            {t("Auto Pay Enabled")}:{" "}
            {formatPaymentMethodDisplay(currentAutoPayMethod)}
          </Typography>
          <Button
            variant="default-outlined"
            disabled={isLoading}
            onClick={handleOpenModal}
          >
            {t("Manage Auto Pay")}
          </Button>
        </AutoPayPanel>
      ) : canEnableAutoPay ? (
        <AutoPayPanel>
          <Typography variant="subtitle1">
            {availablePaymentMethods.length === 1
              ? t(
                  "You have a saved payment method. Enable auto-pay for future invoices?",
                )
              : t(
                  "You have saved payment methods. Enable auto-pay for future invoices?",
                )}
          </Typography>
          <Button variant="primary-filled" onClick={handleOpenModal}>
            {t("Enable Auto Pay")}
          </Button>
        </AutoPayPanel>
      ) : !hasSavedPaymentMethods ? (
        <AutoPayPanel>
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {t(
              "To enable auto-pay for future invoices, check 'Save payment details for future purchases' when paying your next invoice.",
            )}
          </Typography>
        </AutoPayPanel>
      ) : null}

      <Dialog
        open={showAutoPayModal}
        onClose={() => setShowAutoPayModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isAutoPayEnabled
            ? t("Manage Auto Pay")
            : t("Select Payment Method for Auto Pay")}
        </DialogTitle>
        <DialogContent>
          <Stack gap="var(--space-4)">
            <Typography variant="body2">
              {isAutoPayEnabled
                ? t("Change your auto-pay method or turn off auto-pay below.")
                : t(
                    "Select a payment method to enable auto-pay for future invoices.",
                  )}
            </Typography>

            {showAutoPayInformation && (
              <Typography
                variant="body2"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                <Trans
                  i18nKey="EnableAutoPayDescription"
                  components={{
                    1: (
                      <Link
                        href={
                          platform?.termsUrl ||
                          "https://www.outpave.com/account-linking-terms"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Account Linking Terms
                      </Link>
                    ),
                  }}
                />
              </Typography>
            )}

            <RadioGroup
              value={selectedPaymentMethodId}
              onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
            >
              {availablePaymentMethods.map((method) => (
                <Box
                  key={method.id}
                  sx={{
                    paddingBlock: "var(--space-2)",
                    borderBottom:
                      "var(--border-width-1) solid var(--color-border)",
                  }}
                >
                  <FormControlLabel
                    value={method.id}
                    control={<Radio />}
                    // The label sets its own type, so MUI does not wrap this column in a span.
                    disableTypography
                    label={
                      <Stack component="span" gap="0">
                        <Typography variant="body1" component="span">
                          {formatPaymentMethodDisplay(method)}
                        </Typography>
                        {method.id === currentAutoPayMethodId && (
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: "var(--color-text-success)" }}
                          >
                            {t("Current Auto Pay")}
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>

            {showAutoPayInformation && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoPayAgreed}
                    onChange={(e) => setAutoPayAgreed(e.target.checked)}
                  />
                }
                label={t("AgreeToAutoPayTerms")}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="default-outlined" onClick={handleCloseModal}>
            {t("Cancel")}
          </Button>

          {isAutoPayEnabled && (
            <Button
              variant="danger-outlined"
              onClick={removeAutoPayMethod}
              disabled={isLoading}
            >
              {t("Turn Off Auto Pay")}
            </Button>
          )}

          {!isAutoPayEnabled ? (
            <Button
              onClick={setAutoPayMethod}
              disabled={!autoPayAgreed || !selectedPaymentMethodId || isLoading}
              variant="primary-filled"
            >
              {t("Enable Auto Pay")}
            </Button>
          ) : selectedPaymentMethodId !== currentAutoPayMethodId ? (
            <Button
              onClick={setAutoPayMethod}
              disabled={!selectedPaymentMethodId || isLoading || !autoPayAgreed}
              variant="primary-filled"
            >
              {t("Update Auto Pay Method")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AutoPayManager;
